import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { salesApi, salesPin, PlatformApiError } from '../lib/platformApi';
import { MeetingRecorder } from './MeetingRecorder';
import { AskTheRecord } from './AskTheRecord';
import { MarketMap } from './MarketMap';
import { AccountsPanel } from './AccountsPanel';
import { MarketIntel } from './MarketIntel';
import { SalesDashboard } from './SalesDashboard';
import {
  BriefingBlock, FactsBlock, PeopleBlock, PointsBlock, ScoreBlock,
  type Briefing, type Facts, type Contact, type Point, type Score,
} from './ProspectRecord';

/**
 * ═══ THE PIPELINE ═══
 *
 * ⚠ THE MORNING LIST IS THE SCREEN. Everything else here is a list you could keep in a notebook;
 * the reason this exists is the block at the top that says who you promised to ring and have not.
 * If that block is ever pushed below the fold, this has become a filing cabinet.
 *
 * ⚠ AND LOGGING SOMETHING IS ONE ACTION. What happened, and when you will come back to them, in one
 * box with one button. Two steps means the second one does not happen, and a follow-up date nobody
 * sets is the whole problem this is meant to solve.
 */

interface Note { id: string; kind: string; body: string; happenedOn: string; recordedAt: string }
interface Prospect {
  id: string; name: string; city: string | null; kind: string | null; studentCount: number | null;
  stage: string; stageLabel: string; source: string;
  decisionMaker: string | null; decisionMakerRole: string | null;
  mobile: string | null; email: string | null;
  quotedPaise: number | null; nextFollowUpOn: string | null; lostReason: string | null;
  tenantId: string | null; updatedAt: string;
  daysSinceContact: number; isOpen: boolean; notes: Note[];
}
interface Detail {
  prospect: Prospect; facts: Facts;
  contacts: Contact[]; points: Point[]; briefing: Briefing; score: Score;
}
interface NextVisit {
  id: string; name: string; city: string | null; stageLabel: string;
  studentCount: number | null; percent: number; priority: number; because: string;
}
interface Duplicate { id: string; name: string; city: string | null; stage: string; confidence: string; why: string }
interface DuplicateCheck { warning: string | null; matches: Duplicate[] }

interface Attention { prospect: Prospect; reason: string; daysLate: number }
interface StageCount { stage: string; label: string; count: number; quotedPaise: number }
interface Board {
  needsAttention: Attention[]; byStage: StageCount[];
  openQuotedPaise: number; openCount: number;
  lostReasons: { reason: string; count: number }[];
}

const STAGES = ['Identified', 'Contacted', 'DemoScheduled', 'Demoed', 'Quoted',
                'Negotiating', 'Won', 'Lost', 'Dormant'];
const KINDS = ['Call', 'Visit', 'Demo', 'WhatsApp', 'Email', 'Note'];

/** ⚠ Rupees from paise, in the form an Indian founder reads: 1.4L, not ₹140,000.00. */
function money(paise: number | null): string {
  if (!paise) return '—';
  const r = paise / 100;
  if (r >= 10_000_000) return `₹${(r / 10_000_000).toFixed(2)}Cr`;
  if (r >= 100_000) return `₹${(r / 100_000).toFixed(2)}L`;
  if (r >= 1000) return `₹${(r / 1000).toFixed(1)}k`;
  return `₹${r.toFixed(0)}`;
}

const today = () => new Date().toISOString().slice(0, 10);

export function PipelinePanel() {
  const [locked, setLocked] = useState<'checking' | 'no-pin' | 'locked' | 'open'>('checking');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [nextVisits, setNextVisits] = useState<NextVisit[]>([]);
  const [rows, setRows] = useState<Prospect[] | null>(null);
  const [open, setOpen] = useState<Prospect | null>(null);
  const [adding, setAdding] = useState(false);
  const [stageFilter, setStageFilter] = useState('');
  const [search, setSearch] = useState('');
  /**
   * ⚠ THE MAP IS A SEPARATE VIEW, NOT A FILTER ON THE PIPELINE. Thousands of directory rows and
   * tens of prospects are different objects with different jobs; merging them would drown the one
   * list whose purpose is to say what to do this morning.
   */
  // ⚠ The dashboard is the LANDING view, and it is the only one built entirely out of the others.
  const [view, setView] = useState<'today' | 'pipeline' | 'map' | 'accounts' | 'intel'>('today');

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (stageFilter) q.set('stage', stageFilter);
    if (search.trim()) q.set('search', search.trim());
    const [b, list, next] = await Promise.all([
      salesApi.get<Board>('/api/platform/sales/board'),
      salesApi.get<Prospect[]>(`/api/platform/sales/prospects?${q}`),
      // ⚠ Not filtered by the search box. "Where is the next week best spent" is a question about
      // the whole pipeline; narrowing it to whatever is typed in a filter would answer a different
      // question while looking like it answered this one.
      salesApi.get<NextVisit[]>('/api/platform/sales/next-visits?take=6').catch(() => []),
    ]);
    setBoard(b); setRows(list); setNextVisits(next);
  }, [stageFilter, search]);

  // ⚠ The status route answers without a PIN, so the lock screen can say "no PIN is configured on
  // this deployment" instead of showing a box that can never be satisfied.
  const check = useCallback(async () => {
    try {
      const s = await salesApi.get<{ configured: boolean; unlocked: boolean; lockedForSeconds: number }>(
        '/api/platform/sales/status');
      if (!s.configured) { setLocked('no-pin'); return; }
      if (s.unlocked) { setLocked('open'); await load(); return; }
      setLocked('locked');
      if (s.lockedForSeconds > 0)
        setPinError(`Too many wrong PINs. Try again in ${Math.ceil(s.lockedForSeconds / 60)} minute(s).`);
    } catch (e) {
      setLocked('locked');
      setPinError(e instanceof PlatformApiError ? e.message : 'Could not reach the pipeline.');
    }
  }, [load]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => { if (locked === 'open') load().catch(() => undefined); }, [locked, load]);

  const unlock = async () => {
    setPinError(null);
    salesPin.set(pin.trim());
    try {
      await salesApi.get<Board>('/api/platform/sales/board');
      setPin(''); setLocked('open');
    } catch (e) {
      // ⚠ The wrong PIN is cleared from storage, so a stale one cannot sit there failing every
      // request in the background for the rest of the session.
      salesPin.clear();
      setPinError(e instanceof PlatformApiError ? e.message : 'That PIN is not right.');
    }
  };

  if (locked === 'checking') return <p style={{ color: 'var(--ink-3)' }}>Checking…</p>;

  if (locked === 'no-pin') return (
    <div className="card" style={{ padding: 22, maxWidth: 560 }}>
      <div style={{ fontWeight: 650, marginBottom: 6 }}>The pipeline is switched off</div>
      <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0, lineHeight: 1.6 }}>
        No PIN is set on this deployment, so the pipeline refuses every request rather than opening
        without a lock. Set <code>Sales__Pin</code> to at least six characters and restart the API.
      </p>
    </div>
  );

  if (locked === 'locked') return (
    <div className="card" style={{ padding: 22, maxWidth: 420 }}>
      <div style={{ fontWeight: 650, marginBottom: 4 }}>Pipeline PIN</div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 12px', lineHeight: 1.55 }}>
        Separate from your console login, so someone who can provision schools cannot read what you
        quoted them.
      </p>
      <input className="input" type="password" value={pin} autoFocus
        style={{ marginBottom: 10, letterSpacing: '.25em' }}
        onChange={(e) => setPin(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && pin.trim()) void unlock(); }} />
      {pinError && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{pinError}</p>}
      <button className="btn btn-accent" disabled={!pin.trim()} onClick={() => void unlock()}>Unlock</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button className={`btn ${view === 'today' ? 'btn-accent' : ''}`}
                onClick={() => setView('today')}>Today</button>
        <button className={`btn ${view === 'pipeline' ? 'btn-accent' : ''}`}
                onClick={() => setView('pipeline')}>Pipeline</button>
        <button className={`btn ${view === 'map' ? 'btn-accent' : ''}`}
                onClick={() => setView('map')}>The market</button>
        {/* ⚠ A THIRD VIEW, NOT A COLUMN ON THE PIPELINE. Before a school signs the question is
            "what do I do next"; after it signs the question is "what do they owe and when do they
            decide again". Same schools, different jobs. */}
        <button className={`btn ${view === 'accounts' ? 'btn-accent' : ''}`}
                onClick={() => setView('accounts')}>Accounts</button>
        <button className={`btn ${view === 'intel' ? 'btn-accent' : ''}`}
                onClick={() => setView('intel')}>Competition</button>
      </div>

      {view === 'today' && <SalesDashboard onGo={(x) => setView(x as typeof view)} />}
      {view === 'accounts' && <AccountsPanel />}
      {view === 'intel' && <MarketIntel />}

      {view === 'map' && <MarketMap onPromoted={() => { void load(); setView('pipeline'); }} />}

      {view === 'pipeline' && <>
      {/* ── the morning list ── */}
      {board && board.needsAttention.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '3px solid var(--danger)' }}>
          <div style={{ fontWeight: 650, marginBottom: 2 }}>
            {board.needsAttention.length} need{board.needsAttention.length === 1 ? 's' : ''} you
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px' }}>
            Worst first. Overdue promises, then today's, then deals with no next step at all.
          </p>
          {board.needsAttention.map((a) => (
            <button key={a.prospect.id} onClick={() => setOpen(a.prospect)}
              style={{
                display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left',
                background: 'none', border: 0, borderTop: '1px solid var(--line-2)',
                padding: '9px 2px', cursor: 'pointer', font: 'inherit',
              }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{a.prospect.name}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{a.prospect.stageLabel}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: tone(a.reason) }}>{why(a)}</span>
            </button>
          ))}
        </div>
      )}

      {/* ⚠ A SEPARATE LIST FROM THE MORNING ONE, AND THEY ANSWER DIFFERENT QUESTIONS. "Who needs
          you" is about promises you have broken; this is about where the next week is best spent —
          likelihood weighed against size and against how soon the window shuts. Merging them would
          bury the second, which is the one that grows the business. */}
      {nextVisits.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 2 }}>Where the next week is best spent</div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 12px' }}>
            Likelihood weighed against size and against how soon their window shuts.
          </p>
          {nextVisits.map((v) => (
            <button key={v.id} onClick={() => { const row = rows?.find((r) => r.id === v.id); if (row) setOpen(row); }}
              style={{
                display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left',
                background: 'none', border: 0, borderTop: '1px solid var(--line-2)',
                padding: '9px 2px', cursor: 'pointer', font: 'inherit',
              }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{v.name}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {[v.city, v.stageLabel, v.studentCount ? `${v.studentCount} students` : null]
                  .filter(Boolean).join(' · ')}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-2)' }}>{v.because}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 34, textAlign: 'right' }}>
                {v.percent}%
              </span>
            </button>
          ))}
        </div>
      )}

      {board && board.needsAttention.length === 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, fontSize: 13.5, color: 'var(--ink-2)' }}>
          Nothing overdue. Every open deal has a next step in the diary.
        </div>
      )}

      {/* ── the shape of the month ── */}
      {board && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 12 }}>
            <Stat n={String(board.openCount)} label="open" />
            <Stat n={money(board.openQuotedPaise)} label="quoted and still alive" />
          </div>
          {/* ⚠ EVERY stage, including the empty ones. "Quoted: 0" is the most useful thing a founder
              can notice about their own month, and a board that hides it hides that. */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {board.byStage.map((s) => (
              <button key={s.stage} onClick={() => setStageFilter(stageFilter === s.stage ? '' : s.stage)}
                className="btn"
                style={{
                  padding: '4px 9px', fontSize: 12,
                  background: stageFilter === s.stage ? 'var(--brand)' : undefined,
                  color: stageFilter === s.stage ? '#fff' : (s.count ? undefined : 'var(--ink-3)'),
                }}>
                {s.label} <strong style={{ marginLeft: 4 }}>{s.count}</strong>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" placeholder="Search name, city or person…" value={search}
          style={{ maxWidth: 280 }} onChange={(e) => setSearch(e.target.value)} />
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
          + Add a school
        </button>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--line)' }}>
            <Th>School</Th><Th>Stage</Th><Th align="right">Students</Th><Th align="right">Quoted</Th>
            <Th>Next</Th><Th align="right">Last contact</Th>
          </tr></thead>
          <tbody>
            {(rows ?? []).map((p) => (
              <tr key={p.id} onClick={() => setOpen(p)}
                style={{ borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
                         opacity: p.isOpen ? 1 : 0.55 }}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  {p.city && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{p.city}</div>}
                </Td>
                <Td>{p.stageLabel}</Td>
                <Td align="right">{p.studentCount ?? '—'}</Td>
                <Td align="right">{money(p.quotedPaise)}</Td>
                <Td>{p.nextFollowUpOn ?? (p.isOpen ? <span style={{ color: 'var(--danger)' }}>none</span> : '—')}</Td>
                <Td align="right">{p.daysSinceContact >= 9999 ? 'never' : `${p.daysSinceContact}d`}</Td>
              </tr>
            ))}
            {rows?.length === 0 && (
              <tr><Td colSpan={6} muted>Nothing here yet. Add the first school.</Td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ⚠ THE LOST REASONS ARE ON THE SCREEN, not on a report nobody opens. This list is the
          product roadmap and it is worth reading every time you look at the pipeline. */}
      {board && board.lostReasons.length > 0 && (
        <div className="card" style={{ padding: 18, marginTop: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 2 }}>Why they said no</div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 10px' }}>
            In their words. Not grouped — read them and group them yourself.
          </p>
          {board.lostReasons.map((r) => (
            <div key={r.reason} style={{ fontSize: 13.5, padding: '5px 0', borderTop: '1px solid var(--line-2)' }}>
              {r.count > 1 && <strong style={{ marginRight: 6 }}>{r.count}×</strong>}{r.reason}
            </div>
          ))}
        </div>
      )}

      </>}

      {adding && <AddDialog onClose={() => setAdding(false)}
        onDone={() => { setAdding(false); void load(); }} />}
      {open && <ProspectDialog id={open.id} onClose={() => setOpen(null)}
        onChanged={() => void load()} />}
    </div>
  );
}

function why(a: Attention): string {
  switch (a.reason) {
    case 'Overdue': return `${a.daysLate}d overdue`;
    case 'DueToday': return 'due today';
    case 'NoNextStep': return 'no next step';
    case 'GoingCold': return `quiet ${a.daysLate}d`;
    default: return a.reason;
  }
}
function tone(reason: string) {
  return reason === 'Overdue' ? 'var(--danger)' : reason === 'DueToday' ? 'var(--brand)' : 'var(--warn)';
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 650, lineHeight: 1.1 }}>{n}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    name: '', city: '', kind: '', studentCount: '', source: 'Unknown',
    mobile: '', email: '', udiseCode: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dup, setDup] = useState<DuplicateCheck | null>(null);

  /*
   * ⚠ THE CHECK RUNS WHILE YOU TYPE, AND NEVER BLOCKS THE BUTTON.
   *
   * It reports; you decide. A wrong merge silently welds two schools' histories together and may
   * never be noticed, so nothing here refuses anything — except a matching government code, which
   * the API rejects because that is not a judgement call.
   *
   * ⚠ DEBOUNCED, because it fires on every keystroke of a name typed one-handed in a car park.
   */
  useEffect(() => {
    const name = f.name.trim();
    if (name.length < 3) { setDup(null); return; }
    const t = setTimeout(() => {
      salesApi.post<DuplicateCheck>('/api/platform/sales/prospects/check-duplicate', {
        name, city: f.city.trim() || null, mobile: f.mobile.trim() || null,
        email: f.email.trim() || null, udiseCode: f.udiseCode.trim() || null, aisheCode: null,
      }).then(setDup).catch(() => setDup(null));
    }, 400);
    return () => clearTimeout(t);
  }, [f.name, f.city, f.mobile, f.email, f.udiseCode]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.post('/api/platform/sales/prospects', {
        name: f.name.trim(),
        city: f.city.trim() || null,
        kind: f.kind.trim() || null,
        studentCount: f.studentCount.trim() ? Number(f.studentCount) : null,
        source: f.source,
        mobile: f.mobile.trim() || null,
        email: f.email.trim() || null,
        udiseCode: f.udiseCode.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontWeight: 650, marginBottom: 2 }}>Add a school</div>
      {/* ⚠ Only the name is required, and the dialog says so. A school noted in a car park has to be
          enterable in ten seconds or it never gets entered at all. */}
      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 14px' }}>
        Only the name is needed. Everything else can wait.
      </p>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{error}</p>}

      <Field label="Name" v={f.name} on={(v) => setF({ ...f, name: v })} autoFocus />

      {/* ⚠ THE WARNING NAMES THE SCHOOL AND THE REASON. "3 possible duplicates" is a thing you
          dismiss; "Same UDISE code as Excellent CBSE School" is a thing you read. */}
      {dup?.warning && (
        <div style={{
          border: '1px solid var(--line)', borderLeft: '3px solid var(--warn)', borderRadius: 8,
          padding: '8px 10px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.5,
        }}>
          {dup.warning}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
        <Field label="City" v={f.city} on={(v) => setF({ ...f, city: v })} />
        <Field label="Kind" v={f.kind} on={(v) => setF({ ...f, kind: v })} placeholder="CBSE school" />
        <Field label="Students (approx)" v={f.studentCount} on={(v) => setF({ ...f, studentCount: v })} />
        <Field label="Phone" v={f.mobile} on={(v) => setF({ ...f, mobile: v })} />
        <Field label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} />
        {/* ⚠ The only field on this record good enough to say two rows are the same institution. */}
        <Field label="UDISE code" v={f.udiseCode} on={(v) => setF({ ...f, udiseCode: v })} />
        <div>
          <label className="label">How you met</label>
          <select className="input" value={f.source} style={{ width: '100%', marginBottom: 12 }}
            onChange={(e) => setF({ ...f, source: e.target.value })}>
            {['Unknown', 'Referral', 'ColdCall', 'WalkIn', 'Inbound', 'Event', 'ExistingClient']
              .map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" disabled={!f.name.trim() || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Add'}
        </button>
      </div>
    </Overlay>
  );
}

function ProspectDialog({ id, onClose, onChanged }: {
  id: string; onClose: () => void; onChanged: () => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('Call');
  const [happenedOn, setHappenedOn] = useState(today());
  const [next, setNext] = useState('');
  const [stage, setStage] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const detail = await salesApi.get<Detail>(`/api/platform/sales/prospects/${id}/detail`);
    setD(detail);
    const v = detail.prospect;
    setStage(v.stage);

    // ⚠ AN OVERDUE DATE IS NOT PRE-FILLED, and this was a real bug caught by looking at the screen.
    // The field started with whatever was stored, so a school that was 21 days overdue opened with
    // "come back on 23 July" already in the box — a date the input's own min attribute rejects. Log
    // a call without touching it and the same overdue date was written back, so the school you had
    // just rung stayed at the top of the morning list. The one screen whose job is to clear that
    // list was quietly refilling it.
    const stored = v.nextFollowUpOn ?? '';
    setNext(stored && stored >= today() ? stored : '');
  }, [id]);
  useEffect(() => { reload().catch(() => undefined); }, [reload]);

  const log = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/prospects/${id}/log`, {
        kind, body: body.trim(),
        nextFollowUpOn: next || null,
        happenedOn: happenedOn || null,
      });
      setBody('');
      await reload(); onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const move = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/prospects/${id}/stage`, {
        stage, reason: reason.trim() || null, nextFollowUpOn: next || null,
      });
      setReason('');
      await reload(); onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not move it.'); }
    finally { setBusy(false); }
  };

  if (!d) return <Overlay onClose={onClose}><p style={{ color: 'var(--ink-3)' }}>Loading…</p></Overlay>;
  const p = d.prospect;

  return (
    <Overlay onClose={onClose} wide>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
        <div style={{ fontWeight: 650, fontSize: 16 }}>{p.name}</div>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{p.stageLabel}</span>
        {!p.isOpen && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· closed</span>}
      </div>
      {/* ⚠ THE SUB-LINE THAT USED TO BE HERE IS GONE. It said "Mangaluru · CBSE school · 640
          students · quoted ₹1.40L" and the briefing directly below it said the same thing plus the
          staff count and the year — the same facts twice, an inch apart. Found by looking at the
          screen. The briefing headline is the richer one, so it wins. */}
      <div style={{ marginBottom: 12 }} />

      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{error}</p>}

      {/* ⚠ FIRST, ABOVE EVERYTHING. This is what you open thirty seconds before a door opens; a
          briefing you have to scroll to might as well not exist. */}
      {/* ⚠ ABOVE the briefing: it is the one line that says what to do, and the briefing is the
          detail behind it. */}
      <ScoreBlock s={d.score} />
      <BriefingBlock b={d.briefing} />

      {/* ⚠ DIRECTLY UNDER THE BRIEFING, because it answers the questions the briefing raises —
          "what did they object to", "who have we not met" — and a question box further down the
          dialog is one nobody scrolls to. */}
      <AskTheRecord id={id} />

      {/* ── log what happened ── */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
        <label className="label">What happened</label>
        <textarea className="input" rows={3} value={body} placeholder="Met the correspondent. They want the science block to see it first."
          style={{ width: '100%', marginBottom: 8, resize: 'vertical' }}
          onChange={(e) => setBody(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="label">Kind</label>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            {/* ⚠ When it HAPPENED, defaulting to today but editable — a Tuesday visit written up on
                Thursday happened on Tuesday, and dating it Thursday lies about when you last went. */}
            <label className="label">When</label>
            <input className="input" type="date" value={happenedOn} max={today()}
              onChange={(e) => setHappenedOn(e.target.value)} />
          </div>
          <div>
            <label className="label">Come back on</label>
            <input className="input" type="date" value={next} min={today()}
              onChange={(e) => setNext(e.target.value)} />
          </div>
          <button className="btn btn-accent" disabled={!body.trim() || busy} onClick={() => void log()}>
            {busy ? 'Saving…' : 'Log it'}
          </button>
        </div>
        {/* ⚠ Said out loud rather than guessed at. If no date is set, the deal keeps whatever it had
            — which for an overdue one means it stays on the morning list, correctly. Silently
            inventing "a week from now" would clear the list without anyone deciding anything. */}
        {!next && p.isOpen && (
          <p style={{ fontSize: 11.5, color: 'var(--warn)', margin: '8px 0 0' }}>
            {p.nextFollowUpOn
              ? `No new date — this stays due ${p.nextFollowUpOn}.`
              : 'No next step. It will keep appearing on the morning list until you set one.'}
          </p>
        )}
      </div>

      {/* ── move the stage ── */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="label">Stage</label>
            <select className="input" value={stage} onChange={(e) => setStage(e.target.value)}>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="label">
              {stage === 'Lost' ? 'Why did they say no? (required)' : 'Note (optional)'}
            </label>
            <input className="input" value={reason} style={{ width: '100%' }}
              placeholder={stage === 'Lost' ? 'In their words' : ''}
              onChange={(e) => setReason(e.target.value)} />
          </div>
          <button className="btn" disabled={busy || (stage === 'Lost' && !reason.trim())}
            onClick={() => void move()}>Move</button>
        </div>
      </div>

      {/* ⚠ ORDER MATTERS HERE. What they SAID comes before who they are and before the history —
          an input recorded is a buying signal captured, and it is the thing most likely to be
          skipped if it is at the bottom of a long dialog. */}
      {/* ⚠ ABOVE the points, because a recorded meeting is where most points now come from — and a
          recorder you have to scroll past the manual entry to find is one you stop using. */}
      <MeetingRecorder prospectId={id} prospectName={p.name}
                       onChanged={() => { void reload(); onChanged(); }} />
      <PointsBlock id={id} points={d.points} onChanged={() => { void reload(); onChanged(); }} />
      <PeopleBlock id={id} contacts={d.contacts} onChanged={() => { void reload(); onChanged(); }} />
      <FactsBlock key={p.updatedAt} id={id} facts={d.facts}
                  onChanged={() => { void reload(); onChanged(); }} />

      {/* ── the history ── */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>History</div>
        {p.notes.map((n) => (
          <div key={n.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderTop: '1px solid var(--line-2)' }}>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)', minWidth: 82, fontVariantNumeric: 'tabular-nums' }}>
              {n.happenedOn}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)', minWidth: 62 }}>
              {n.kind === 'StageChange' ? '' : n.kind}
            </span>
            <span style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{n.body}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ---------------- small shared bits ----------------

function Field({ label, v, on, placeholder, autoFocus }: {
  label: string; v: string; on: (v: string) => void; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={v} placeholder={placeholder} autoFocus={autoFocus}
        style={{ width: '100%', marginBottom: 12 }} onChange={(e) => on(e.target.value)} />
    </div>
  );
}

function Overlay({ children, onClose, wide }: { children: ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50,
      display: 'grid', placeItems: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
        style={{ width: wide ? 640 : 440, maxWidth: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

function Th({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)',
                      textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: {
  children?: ReactNode; colSpan?: number; muted?: boolean; align?: 'right';
}) {
  return <td colSpan={colSpan} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)',
                                        textAlign: muted ? 'center' : (align ?? 'left'), fontSize: 13 }}>{children}</td>;
}
